import { useEffect, useState } from "react";
import { Plus, Loader2, X, Trash2, Copy, Wallet, Link2, CheckCircle2, Percent, KeyRound, QrCode } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fcfa } from "../../lib/format";
import { QRCodeBox } from "../../components/shop/QRCodeBox";
import { toast } from "sonner";

const inputCls = "border border-brand-line rounded-xl px-4 py-2.5 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none bg-white text-brand-ink";

export default function Partners() {
  const { user } = useAuth();
  const isPatron = user.role === "patron";
  const [partners, setPartners] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qr, setQr] = useState(null);

  const load = () => api.get("/admin/partners").then(({ data }) => setPartners(data));
  useEffect(() => { load(); }, []);

  const shopUrl = (code) => `${window.location.origin}/boutique?ref=${code}`;
  const copyLink = (code) => { navigator.clipboard.writeText(shopUrl(code)); toast.success("Lien copié !"); };

  const payAll = async (p) => {
    if (!window.confirm(`Marquer ${fcfa(p.commission_due)} comme payé à ${p.name} ?`)) return;
    try { await api.post(`/admin/partners/${p.id}/pay-commissions`); toast.success("Commissions marquées payées"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le partenaire ${p.name} ?`)) return;
    try { await api.delete(`/admin/partners/${p.id}`); toast.success("Partenaire supprimé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!partners) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>;

  const totalDue = partners.reduce((s, p) => s + (p.commission_due || 0), 0);

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Partenaires & commissions</h1>
          <p className="text-brand-sub text-sm mt-1">Revendeurs qui partagent leur lien et gagnent une commission sur chaque vente.</p>
        </div>
        {isPatron && (
          <button onClick={() => setCreating(true)} data-testid="add-partner-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-5 py-2.5 font-semibold flex items-center gap-2">
            <Plus className="w-5 h-5" /> Nouveau partenaire
          </button>
        )}
      </div>

      <div className="bg-brand-ink text-white rounded-2xl p-5 flex items-center gap-4">
        <Wallet className="w-8 h-8 text-brand-gold" />
        <div>
          <p className="text-sm text-white/70">Commissions à payer (total)</p>
          <p className="font-heading font-black text-2xl" data-testid="total-commission-due">{fcfa(totalDue)}</p>
        </div>
      </div>

      {partners.length === 0 ? (
        <p className="text-brand-muted text-sm">Aucun partenaire pour l'instant.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {partners.map((p) => (
            <div key={p.id} data-testid={`partner-card-${p.id}`} className="bg-white border border-brand-line rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-bold text-brand-ink text-lg">{p.name}</p>
                  <p className="text-sm text-brand-muted">{p.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-brand-cream text-brand-ink font-bold">Code : {p.code}</span>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold flex items-center gap-1"><Percent className="w-3 h-3" />{p.commission_rate}%</span>
                    {p.active === false && <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600">Inactif</span>}
                  </div>
                </div>
                {isPatron && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(p)} data-testid={`edit-partner-${p.id}`} title="Modifier" className="p-2 text-brand-sub hover:bg-brand-cream rounded-lg"><KeyRound className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} data-testid={`delete-partner-${p.id}`} className="p-2 text-brand-secondary hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-brand-cream rounded-xl py-2">
                  <p className="text-xs text-brand-muted">Ventes</p>
                  <p className="font-bold text-brand-ink text-sm">{fcfa(p.total_sales)}</p>
                </div>
                <div className="bg-amber-50 rounded-xl py-2">
                  <p className="text-xs text-amber-700">Commission due</p>
                  <p className="font-bold text-amber-800 text-sm" data-testid={`due-${p.id}`}>{fcfa(p.commission_due)}</p>
                </div>
                <div className="bg-green-50 rounded-xl py-2">
                  <p className="text-xs text-green-700">Déjà payé</p>
                  <p className="font-bold text-green-800 text-sm">{fcfa(p.commission_paid)}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => copyLink(p.code)} data-testid={`copy-link-${p.id}`} className="flex-1 border border-brand-line rounded-full px-3 py-2 text-sm font-medium text-brand-sub hover:bg-brand-cream flex items-center justify-center gap-1.5"><Link2 className="w-4 h-4" /> Copier le lien</button>
                <button onClick={() => setQr(p)} data-testid={`qr-${p.id}`} className="flex-1 border border-brand-line rounded-full px-3 py-2 text-sm font-medium text-brand-sub hover:bg-brand-cream flex items-center justify-center gap-1.5"><QrCode className="w-4 h-4" /> QR code</button>
                {isPatron && p.commission_due > 0 && (
                  <button onClick={() => payAll(p)} data-testid={`pay-${p.id}`} className="flex-1 bg-green-600 text-white rounded-full px-3 py-2 text-sm font-semibold hover:bg-green-700 flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Marquer payé</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <PartnerModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <EditPartnerModal partner={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {qr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setQr(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-xl text-brand-ink">QR code — {qr.name}</h3>
              <button onClick={() => setQr(null)} className="p-1.5 hover:bg-brand-cream rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <QRCodeBox value={shopUrl(qr.code)} fileName={`qr-noumabou-${qr.code}`} caption={`Scannez pour acheter · Code ${qr.code}`} />
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "", code: "", commission_rate: 10, phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setError("");
    setLoading(true);
    try {
      await api.post("/admin/partners", { ...f, code: f.code.toUpperCase(), commission_rate: Number(f.commission_rate) });
      toast.success("Partenaire créé");
      onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">Nouveau partenaire</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-brand-cream rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input data-testid="partner-name" className={inputCls} placeholder="Nom du partenaire" value={f.name} onChange={set("name")} />
          <input data-testid="partner-email" type="email" className={inputCls} placeholder="E-mail (pour se connecter)" value={f.email} onChange={set("email")} />
          <input data-testid="partner-password" type="text" className={inputCls} placeholder="Mot de passe (min. 6)" value={f.password} onChange={set("password")} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="partner-code" className={inputCls} placeholder="Code (ex: AWA)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} />
            <div className="relative">
              <input data-testid="partner-rate" type="number" className={inputCls + " pr-8"} placeholder="Commission" value={f.commission_rate} onChange={set("commission_rate")} />
              <span className="absolute right-3 top-2.5 text-brand-muted text-sm">%</span>
            </div>
          </div>
          {error && <p className="text-sm text-brand-secondary">{error}</p>}
          <button onClick={save} disabled={loading} data-testid="save-partner-button" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer le partenaire"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditPartnerModal({ partner, onClose, onSaved }) {
  const [rate, setRate] = useState(partner.commission_rate);
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(partner.active !== false);
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      const payload = { commission_rate: Number(rate), active };
      if (password) payload.password = password;
      await api.patch(`/admin/partners/${partner.id}`, payload);
      toast.success("Partenaire mis à jour");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">Modifier {partner.name}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-brand-cream rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-brand-sub">Taux de commission (%)</label>
            <input data-testid="edit-rate" type="number" className={inputCls + " mt-1"} value={rate} onChange={(e) => setRate(e.target.value)} />
          </div>
          <div>
            <label className="text-sm text-brand-sub">Nouveau mot de passe (optionnel)</label>
            <input data-testid="edit-password" type="text" className={inputCls + " mt-1"} placeholder="Laisser vide pour ne pas changer" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-brand-ink">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="edit-active" /> Partenaire actif
          </label>
          <button onClick={save} disabled={loading} data-testid="save-edit-partner" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
